/**
 * The facts a failed call carries, for the error panel's foot "Error {status} · ref {id}" (PD §5, spec §J4). Every value is read
 * from the caught error itself — the prototype's fixed "Error 503 · ref 7F2A-19" (P/proto-states.js:65) is a placeholder this
 * never imitates: a part the error does not carry is omitted, and with neither the foot is absent.
 *
 * Shapes: graphql-request's `ClientError` (thrown by `GraphQLDataProvider.ExecuteGQL`) carries `response.status` (the HTTP status)
 * and `response.errors[]` (the GraphQL errors, whose `extensions.requestID` is the server's request id when it sends one).
 */
interface ClientErrorLike {
    readonly response?: {
        readonly status?: unknown;
        readonly errors?: ReadonlyArray<{ readonly extensions?: { readonly requestID?: unknown } }>;
    };
}

function AsClientError(error: unknown): ClientErrorLike | null {
    return typeof error === 'object' && error !== null ? (error as ClientErrorLike) : null;
}

/** The first GraphQL error's `extensions.requestID` when it is a non-empty string, else null. */
export function RequestIdFrom(error: unknown): string | null {
    const errors = AsClientError(error)?.response?.errors;
    const id = Array.isArray(errors) ? errors[0]?.extensions?.requestID : undefined;
    return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * The HTTP status on a graphql-request `ClientError` (`response.status`) when the HTTP layer FAILED (an integer of 400 or more), else
 * null. A GraphQL error rides a successful 200 response; "Error 200" would be a false claim, so only its request id shows.
 */
export function StatusFrom(error: unknown): number | null {
    const status = AsClientError(error)?.response?.status;
    return typeof status === 'number' && Number.isInteger(status) && status >= 400 ? status : null;
}

/** "Error {status} · ref {id}", "Error {status}", "ref {id}", or null when the error carries neither. */
export function ErrorFoot(status: number | null, requestId: string | null): string | null {
    const parts: string[] = [];
    if (status !== null) parts.push(`Error ${status}`);
    if (requestId !== null) parts.push(`ref ${requestId}`);
    return parts.length > 0 ? parts.join(' · ') : null;
}
